import { FC, useId, useState } from "react";

import { Button, Buttons, Icon, Modal, OverlayContainer, Tooltip, TooltipStyle } from "../../general";

import { createTokenStyleHook } from "../../tokens";
import { Paragraph } from "../../typography";

import { BusyIndicator } from "./components/BusyIndicator";
import { ChatHeader } from "./components/ChatHeader";
import { ConversationContent } from "./components/ConversationContent";
import { DisclaimerNotice } from "./components/DisclaimerNotice";
import { isEmptyAssistantTurn } from "./components/DiscussionView";
import { PromptFooter } from "./components/PromptFooter";
import { UserInput } from "./components/UserInput";
import { useAgentChatbotController } from "./hooks/useAgentChatbotController";
import { resolveDockStyle } from "./cornerDock";
import { AgentChatbotProps } from "./types";
import tokenStyles from "./styles.module.css";

export type { AgentChatAttachment, AgentChatMessage, AgentChatSubmission, AgentChatbotProps } from "./types";

const useStyles = createTokenStyleHook<AgentChatbotProps>({
  tokenStyles,
  componentTokens: "agent_chatbot",
});

/* Shown unless a consumer overrides or suppresses it, so every deployment carries the advisory by
   default rather than by remembering to opt in. */
const DEFAULT_DISCLAIMER = "Judi can make mistakes. Verify important information before acting on it; you are responsible for any actions taken through this chatbot.";

export const AgentChatbot: FC<AgentChatbotProps> = (props) => {
  const {
    open,
    onClose,
    onOpen,
    onClear,
    layout = "standalone",
    dock,
    reservedBottomSpace,
    title = "Ask Judi",
    introTitle = "How can I assist you?",
    introSubtitle = "Not sure where to start? Try asking me…",
    introPresets,
    disclaimer = DEFAULT_DISCLAIMER,
    disclaimerLabel = "Disclaimer",
    onDisclaimerDismiss,
    notice,
    noticeLabel,
    onNoticeDismiss,
    onPresetSelect,
    onSuggestionSelect,
    messages,
    isLoading = false,
    loadingStatusLabel,
    hasError = false,
    errorMessage = "Unable to connect to Judi's AI service",
    onRetry,
    attachedFiles,
    onRemoveFile,
    onAttachFile,
    onFilesSelected,
    inputValue,
    onInputChange,
    onSubmit,
    onFileDrop,
    isUploadTargetVisible = false,
    isAttachmentEnabled = true,
    footnote,
  } = props;

  const { mode, styles, StyleModeBoundary, cx } = useStyles(props);

  const panelTitleId = useId();

  const [isDisclaimerDismissed, setIsDisclaimerDismissed] = useState(false);
  const [isNoticeDismissed, setIsNoticeDismissed] = useState(false);
  const [isClearConfirmVisible, setIsClearConfirmVisible] = useState(false);

  const isDisclaimerVisible = !!disclaimer && !isDisclaimerDismissed;
  const isNoticeVisible = !!notice && !isNoticeDismissed;
  const hasNotices = isDisclaimerVisible || isNoticeVisible;

  const {
    currentMessages,
    currentAttachedFiles,
    currentInputValue,
    hasMessages,
    canSubmit,
    loadingNotice,
    scrollRegionRef,
    isScrolledUp,
    handleScroll,
    scrollToLatest,
    panelRef,
    panelHeight,
    minHeight,
    onGrabberPointerDown,
    onGrabberKeyDown,
    textareaRef,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleSubmit,
    handleFilesSelected,
    handleFileDrop,
    handleRemoveFile,
    populateInput,
  } = useAgentChatbotController({
    messages,
    isLoading,
    hasError,
    attachedFiles,
    onFilesSelected,
    onFileDrop,
    onRemoveFile,
    inputValue,
    onInputChange,
    onSubmit,
  });

  const handleClose = () => onClose?.();

  const handleDisclaimerDismiss = () => {
    setIsDisclaimerDismissed(true);
    onDisclaimerDismiss?.();
  };

  const handleNoticeDismiss = () => {
    setIsNoticeDismissed(true);
    onNoticeDismiss?.();
  };

  const handleClearRequest = () => setIsClearConfirmVisible(true);

  const handleClearConfirmClose = (isApproved: boolean) => {
    setIsClearConfirmVisible(false);
    if (!isApproved) return;

    /* Clearing starts a new conversation, so an earlier dismissal is forgotten and the advisory
       is shown again. */
    setIsDisclaimerDismissed(false);
    setIsNoticeDismissed(false);
    onClear?.();
  };

  const requestClear = onClear ? handleClearRequest : undefined;

  const handlePresetSelect = (preset: string) => {
    populateInput(preset);
    onPresetSelect?.(preset);
  };

  const handleSuggestionSelect = (suggestion: string) => {
    populateInput(suggestion);
    onSuggestionSelect?.(suggestion);
  };

  const renderPromptTextbox = () => (
    <UserInput
      styles={styles}
      textareaRef={textareaRef}
      currentInputValue={currentInputValue}
      canSubmit={canSubmit}
      attachedFiles={currentAttachedFiles}
      loadingNotice={loadingNotice}
      isUploadTargetVisible={isUploadTargetVisible ?? false}
      isAttachmentEnabled={isAttachmentEnabled ?? true}
      onAttachFile={onAttachFile || undefined}
      onFilesSelected={handleFilesSelected}
      onRemoveFile={handleRemoveFile}
      onFileDrop={handleFileDrop}
      onTextareaChange={handleTextareaChange}
      onTextareaKeyDown={handleTextareaKeyDown}
      onSubmit={handleSubmit}
      hasError={hasError ?? false}
      onRetry={onRetry || undefined}
    />
  );

  const renderFooter = () => (
    <PromptFooter
      styles={styles}
      cx={cx}
      hasError={Boolean(hasError)}
      errorMessage={errorMessage}
    >
      {renderPromptTextbox()}
    </PromptFooter>
  );

  /* The transcript already ends with an assistant turn while a reply streams in, and that turn
     renders its own avatar, so a second one on the indicator would show the agent twice. A turn
     that has not received any content yet is not rendered at all, so the indicator keeps its own. */
  const lastMessage = currentMessages[currentMessages.length - 1];
  const hasTrailingAssistantTurn = lastMessage?.role === "assistant" && !isEmptyAssistantTurn(lastMessage);

  const renderBody = () => (
    <div className={styles.AgentChatbotContent}>
      <div
        ref={scrollRegionRef}
        className={styles.AgentChatbotScrollRegion}
        onScroll={handleScroll}
        {...(hasNotices ? { "data-has-notices": true } : {})}
      >
        {/* Outside <ConversationContent> so the stack covers the welcome state and the transcript alike. */}
        {!!hasNotices && (
          <div className={styles.AgentChatbotNotices}>
            {!!isNoticeVisible && (
              <DisclaimerNotice
                styles={styles}
                appearance="info"
                content={notice}
                label={noticeLabel}
                dismissLabel="Dismiss notice"
                testId="chatbot-notice"
                onDismiss={handleNoticeDismiss}
              />
            )}
            {!!isDisclaimerVisible && (
              <DisclaimerNotice
                styles={styles}
                content={disclaimer}
                label={disclaimerLabel}
                onDismiss={handleDisclaimerDismiss}
              />
            )}
          </div>
        )}
        <section className={styles.AgentChatbotConversation}>
          <ConversationContent
            styles={styles}
            cx={cx}
            hasMessages={hasMessages}
            currentMessages={currentMessages}
            introTitle={introTitle}
            introSubtitle={introSubtitle}
            introPresets={introPresets || undefined}
            currentInputValue={currentInputValue}
            onPresetSelect={handlePresetSelect}
            onSuggestionSelect={handleSuggestionSelect}
          />
        </section>
      </div>
      {/* Pinned below the scroll region so the dots always sit just above the input. */}
      {!!(isLoading && hasMessages) && (
        <BusyIndicator
          styles={styles}
          statusLabel={loadingStatusLabel}
          showAvatar={!hasTrailingAssistantTurn}
        />
      )}
      {!!(isScrolledUp && hasMessages) && (
        <Tooltip content="Scroll to latest message">
          {(tooltipRef) => (
            <button
              ref={tooltipRef}
              type="button"
              className={styles.AgentChatbotScrollToBottom}
              aria-label="Scroll to latest message"
              onClick={scrollToLatest}
              data-testid="scroll-to-bottom"
            >
              <Icon name="chevron-down" className={styles.AgentChatbotScrollToBottomIcon} aria-hidden="true" />
            </button>
          )}
        </Tooltip>
      )}
    </div>
  );

  /* Both layouts share the same header; only the floating panel can be dismissed, so the embedded
     one simply has no close control. */
  const renderHeaderContent = () => (
    <ChatHeader
      styles={styles}
      title={title}
      titleId={panelTitleId}
      onClear={requestClear}
      onClose={layout === "standalone" ? handleClose : undefined}
    />
  );

  /* Panel chrome shared by both layouts: the resize grabber, header, conversation body, input
     footer, and optional footnote. The overlay container is panel-local because the app-level one
     sits below the docked panel's z-index, which would render the options menu behind the chat. */
  const renderPanelContent = () => (
    <TooltipStyle alignment="top">
      <OverlayContainer>
        <div
          className={styles.AgentChatbotGrabberStrip}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize chat panel"
          aria-valuenow={panelHeight || undefined}
          aria-valuemin={minHeight}
          tabIndex={0}
          onPointerDown={onGrabberPointerDown}
          onKeyDown={onGrabberKeyDown}
          data-testid="chatbot-grabber"
        >
          <div className={styles.AgentChatbotGrabber} aria-hidden="true" />
        </div>
        {!!title && (
          <div className={styles.AgentChatbotHeader}>
            {renderHeaderContent()}
          </div>
        )}
        {renderBody()}
        {renderFooter()}
        {!!footnote && (
          <div className={styles.AgentChatbotFootnote}>
            <p className={styles.AgentChatbotFootnoteText}>{footnote}</p>
          </div>
        )}
        <Modal.Approve
          title="Clear conversation"
          visible={isClearConfirmVisible}
          approveContent="Clear conversation"
          approveButtonAppearance={Buttons.Destructive.Primary.Large}
          denyContent="Cancel"
          onClose={handleClearConfirmClose}
          testId="chatbot-clear-confirm"
        >
          <Paragraph>This removes every message in this chat and starts a new conversation. It cannot be undone.</Paragraph>
        </Modal.Approve>
      </OverlayContainer>
    </TooltipStyle>
  );

  /* "embedded" renders inline and is always visible; "standalone" is a floating dialog gated by
     `open`. Both share the same panel chrome and resize behavior. */
  const renderPanel = () => {
    const panelStyle = panelHeight !== null ? { height: `${panelHeight}px` } : undefined;
    if (layout !== "standalone") {
      return (
        <section
          ref={panelRef}
          data-layout="embedded"
          className={styles.AgentChatbotPanel}
          style={panelStyle}
          aria-labelledby={panelTitleId}
          data-testid="chatbot-panel"
        >
          {renderPanelContent()}
        </section>
      );
    }

    if (!open) return null;

    return (
      <dialog
        open
        ref={panelRef}
        data-layout="standalone"
        aria-modal="false"
        className={styles.AgentChatbotPanel}
        style={panelStyle}
        aria-labelledby={panelTitleId}
        data-testid="chatbot-panel"
      >
        {renderPanelContent()}
      </dialog>
    );
  };

  /* The shell fills its container by default; when docked, the fixed wrapper (below) supplies the
     bottom-right position and the min-height-protected height. */
  const renderShell = () => (
    <div className={styles.AgentChatbot}>
      {renderPanel()}
      {layout === "standalone" && (
        <Button
          appearance="primary"
          className={styles.AgentChatbotTrigger}
          icon="message-dots"
          aria-label={open ? `Close Chatbot` : `Open Chatbot`}
          onClick={() => (open ? handleClose() : onOpen?.())}
          testId="chatbot-trigger"
        />
      )}
    </div>
  );

  const dockStyle = dock === "bottom-right" && layout === "standalone" ? resolveDockStyle(reservedBottomSpace) : undefined;

  return (
    <StyleModeBoundary mode={mode}>
      {dockStyle ? <div style={dockStyle}>{renderShell()}</div> : renderShell()}
    </StyleModeBoundary>
  );
};
